import { reactive, watch, ref, unref, toRefs, computed, nextTick, set } from 'vue'
import { Message } from 'view-design'
import { MessageBox } from 'element-ui'
import moment from 'moment'
import { clone, groupBy } from '@/common/js/just.js'
import { shuffle } from '@/common/js/utils.js'
import { MEDIAS } from '@/common/js/constants.js'
import { getCreativeFormModel } from '../model/form.js'
import { makeSelectorForm } from '@/components/material-selector/index.js'
import { MATERIAL_TYPE_SIZES_V3, MATERIAL_IMAGE_GROUP_TYPE } from '@/views/create/gdt/model/creative-const.js'
import { MATERIAL_TYPE_ID, MATERIAL_TYPE, MATERIAL_TYPE_NAME } from '@/components/material-selector/consts.js'
import { dataFormatter } from '@/components/material-selector/utils.js'
import { IMG_URL_MD5_REGEXP } from '@/views/create/gdt/model/project-const.js'
import { uploadMetarial } from '@/api/material.model'
import {
  IMAGE_TYPES,
  IMAGE_STYLE
} from '@/views/material/uploading/consts.js'

import { getCreativesByImageCodes, queryImageDetailByIds } from '@/api/common.model'

let increasingGroupIndex = 0
const getMaterialSizeKey = (material) => `${Number(material.width)}*${Number(material.height)}`

export const hasSameMaterialSize = (materials) => {
  if (!materials.length || materials.some((material) => Number(material.width) <= 0 || Number(material.height) <= 0)) return false
  return new Set(materials.map(getMaterialSizeKey)).size === 1
}

const getDistributeFormModel = () => {
  return {
    group: 2, // 分配多少个创意组数量
    groupAsSameImageId: false,
    [MATERIAL_TYPE.VIDEO]: '', // 每个创意组视频数量
    [MATERIAL_TYPE.IMAGE]: '', // 每个创意组图片数量
    [MATERIAL_TYPE.GROUP_IMAGE]: '' // 每个创意组组图数量
  }
}

/**
 * @description: 创意组
 */
export const useCreativeGroups = (configForm) => {
  const groupModel = reactive({
    currCreativeGroupIndex: 0,
    creativeFormGroups: []
  })

  const creativeFormRef = ref([])

  const currGroup = computed({
    get() {
      return groupModel.creativeFormGroups[groupModel.currCreativeGroupIndex]
    },
    set(g) {
      groupModel.creativeFormGroups.splice(groupModel.currCreativeGroupIndex, 1, g)
    }
  })

  // 新建计划时上传组图后，获取组图缩略图
  setInterval(async () => {
    const groups = [...groupModel.creativeFormGroups]

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i]
      const { image_list } = group.selectedMaterials || {}

      if (image_list?.length) {
        // 获取组图占位项id
        const ids = image_list.filter(img => img.placeholder === true).map(img => img.id)
        if (ids.length) {
          // 获取缩略图详情
          let imageLists = await queryImageDetailByIds({ imageDetailIds: ids })
          imageLists = imageLists.map(item => item.items[0])
          for (let j = 0; j < imageLists.length; j++) {
            // 如果没有缩略图地址，跳过，等待下次轮询
            if (!imageLists[j]?.overview_addr) continue
            const image = dataFormatter.group_image(imageLists[j])
            const index = image_list.findIndex(img => Number(img.id) === image.id)
            // 替换占位项内容
            if (index > -1) {
              group.selectedMaterials.image_list[index] = { ...image }
            }
          }
        }
      }
    }

    groupModel.creativeFormGroups = []
    nextTick(() => {
      groupModel.creativeFormGroups = groups
    })
  }, 5000)

  /**
   * @description: 新增一个创意组
   * @return {*}
   */
  const onCreativeGroupAdd = () => {
    increasingGroupIndex++
    const creative_name = `创意组${increasingGroupIndex}`
    const creativeFormItem = getCreativeFormModel()

    // 为图文素材添加默认账号，默认为已选账号的第一个
    // creativeFormItem.imageAudioAccount = getAdAccount()
    groupModel.creativeFormGroups.push({
      ...creativeFormItem,
      creative_name
    })
    groupModel.currCreativeGroupIndex = groupModel.creativeFormGroups.length - 1
  }
  /**
   * @description: 删除一个创意组
   * @param {*} index
   * @return {*}
   */
  const onCreativeGroupItemRemove = (index) => {
    groupModel.creativeFormGroups = groupModel.creativeFormGroups.filter((item, i) => i !== index)
    groupModel.currCreativeGroupIndex = index - 1 < 0 ? 0 : index - 1
  }

  /**
   * @description: 复制一个创意组
   * @return {*}
   */
  const onCreativeGroupDuplicate = (index) => {
    if (groupModel.creativeFormGroups.length >= 200) return

    const dupItem = clone(groupModel.creativeFormGroups[index])
    dupItem.creative_name += '_复制'
    groupModel.creativeFormGroups.splice(index + 1, 0, dupItem)
    groupModel.currCreativeGroupIndex = index + 1
  }

  const onBeforeConfigConfirm = async (done) => {
    let errorIndex = -1
    try {
      for (const index in creativeFormRef.value) {
        const formRef = creativeFormRef.value[index]
        errorIndex = index
        await formRef.validate()
      }

      done()
    } catch (e) {
      console.log('onBeforeConfigConfirm creative validate error output: ', e)
      groupModel.currCreativeGroupIndex = Number(errorIndex)
    }
  }

  /**
   * @description: 全量更新当前创意组
   * @param {*} form
   */
  const onUpdateCreativeForm = (form) => {
    creativeFormRef.value[groupModel.currCreativeGroupIndex].setForm(form)
  }

  return {
    ...toRefs(groupModel),
    creativeFormRef,
    currGroup,
    onUpdateCreativeForm,
    onBeforeConfigConfirm,
    onCreativeGroupAdd,
    onCreativeGroupItemRemove,
    onCreativeGroupDuplicate
  }
}

/**
 * @description: 创意素材弹窗
 * @param {*} getAdAccount 获取当前已选账号的第一个账号
 * @param {*} currGroup 当前创意组
 * @param {*} creativeFormGroups 创意组
 * @param {*} onUpdateCreativeForm 更新当前创意组
 * @return {*}
 */
export const useCreativeSelector = (getAdAccount, currGroup, creativeFormGroups, onUpdateCreativeForm, configForm, updateConfigForm) => {
  const selectorModel = reactive({
    // onSelectorClose 会重置此属性
    isDistributionMode: false,
    // 上传组图模式
    isUploadGroupImageMode: false,
    // 选择组图模式
    isSelectGroupImageMode: false,
    // 选择试玩结束页
    isSelectWxgamePlayableMode: false,
    // 轮播三图/三视频素材类型
    carouselMaterialType: '',
    groupImageType: '',
    isNormalMode: false,
    // 创意组分配
    distributeForm: getDistributeFormModel(),
    // 重新打乱创意组分配
    recombineDistrubuteForm: getDistributeFormModel(),
    showSelectorModal: false,
    recombineLoading: false,

    // 保存替换素材时的辅助参数，onSelectorClose 会重置对象内属性
    replaceProps: {
      materialType: null,
      materialTabIndex: null,
      groupImageIndex: -1
    },
    selectorProps: {
      adAccountId: 0,
      max: 15,
      videoId: 0, // 获取视频封面对应的视频id，替换已选视频封面时传入
      assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO], // 1单图 2双图 3三图 4视频 6头条视频 7头条图片 8LOGO 12收藏 13组图
      form: {}
    }
  })

  // 监听配置变化，更新一键打乱时素材个数配置，
  // 只会在src/views/create/bytedance/group.vue onTemplateConfigConfirm 切换模板时触发
  watch(
    () => configForm.value.recombineHelpers.distributeForm,
    (val) => {
      if (val?.group) {
        selectorModel.recombineDistrubuteForm = { ...getDistributeFormModel(), ...val }
      }
    }
  )

  const selectedMaterialLength = computed(() => {
    if (!currGroup.value) return 0
    const { selectedMaterials } = currGroup.value

    const imageLen = selectedMaterials.image?.length || 0
    const videoLen = selectedMaterials.video?.length || 0
    const imageListLen = selectedMaterials.image_list?.length || 0
    return imageLen + videoLen + imageListLen
  })
  // 素材类型是否禁用逻辑
  const seletedMaterialRadioDiasbled = computed(() => {
    const { isDistributionMode, isNormalMode, isSelectGroupImageMode, isUploadGroupImageMode, isSelectWxgamePlayableMode, replaceProps } = selectorModel
    // 是否素材替换
    const isReplaceMode = !!replaceProps.materialType

    const states = {
      [MATERIAL_TYPE.IMAGE]: true,
      [MATERIAL_TYPE.GROUP_IMAGE]: true,
      [MATERIAL_TYPE.VIDEO]: true,
      [MATERIAL_TYPE.POSTER]: true
    }

    // 正常选择素材时、批量添加创意组可以切换视频、图片、图文、收藏
    if (isNormalMode || isDistributionMode) {
      states[MATERIAL_TYPE.IMAGE] = false
      states[MATERIAL_TYPE.VIDEO] = false
      states[MATERIAL_TYPE.GROUP_IMAGE] = false
    }

    // 如果是上传组图模式时，不能直接选择组图，只能选择图片
    if (isUploadGroupImageMode) {
      states[MATERIAL_TYPE.IMAGE] = false
    }

    if (isSelectWxgamePlayableMode) {
      states[MATERIAL_TYPE.IMAGE] = false
    }

    // 如果是选择组图模式时，不能直接选择组图，只能选择图片
    if (isSelectGroupImageMode) {
      states[MATERIAL_TYPE.GROUP_IMAGE] = false
    }

    // 替换时，只能选择对应的素材类型
    if (isReplaceMode) {
      states[replaceProps.materialType] = false

      // 如果替换类型为封面，可以将图片radio放开，让用户可以选择图片作为封面
      if (replaceProps.materialType === MATERIAL_TYPE.POSTER) {
        states[MATERIAL_TYPE.IMAGE] = false
      }
    }

    return states
  })

  /**
   * @description: 根据项目基础信息选择的素材标签构造一个素材选择弹窗的过滤条件
   * @param {*} computed
   * @return {*}
   */
  const materialTagList = computed(() => {
    const { material_package_id, material_package_name } = configForm.value.budgetForm
    return material_package_id ? [{ id: material_package_id, name: material_package_name }] : []
  })

  /**
   * @description: 替换已选素材
   * @param {*} materialType 需替换的素材类型
   * @param {*} materialTabIndex 需替换的素材tab所在的索引
   * @param {*} groupImageProps 组图替换参数：asset 组图替换素材 groupIndex 组图内的素材索引
   */
  const onShowRepalceMaterialSelector = (materialTabIndex, materialType, groupImageProps) => {
    const { asset, groupIndex } = groupImageProps || {}
    selectorModel.selectorProps = {
      ...selectorModel.selectorProps,
      max: 1,
      videoId: 0,
      assetType: MATERIAL_TYPE_ID[materialType],
      form: { ...makeSelectorForm(), sizes: [], media_id: MEDIAS.GDT }
    }

    selectorModel.replaceProps.materialType = materialType
    selectorModel.replaceProps.materialTabIndex = materialTabIndex
    if (asset) {
      selectorModel.selectorProps.form.sizes = [`${asset.width}*${asset.height}`]
      selectorModel.replaceProps.groupImageIndex = groupIndex
    }

    // 替换封面
    if (materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.POSTER]) {
      const { id: videoId } = currGroup.value.selectedMaterials.video[materialTabIndex]
      // 替换封面时，需要传入对应的视频id
      selectorModel.selectorProps.videoId = videoId
    }

    selectorModel.showSelectorModal = true
  }

  /**
   * @description: 显示素材选择弹窗
   * @param {isDistribute} 是否是创意组智能分配
   * @return {*}
   */
  const onShowCreativeSelector = (e, isWxGamePlayable = false, carouselMaterialType = '') => {
    selectorModel.isSelectWxgamePlayableMode = isWxGamePlayable
    selectorModel.carouselMaterialType = carouselMaterialType

    if (e === true) {
      selectorModel.isDistributionMode = true
    } else if (Object.values(MATERIAL_IMAGE_GROUP_TYPE).includes(e)) {
      // 上传组图
      selectorModel.isUploadGroupImageMode = true
      selectorModel.groupImageType = e
    } else if (e === 0) {
      // 选择组图
      selectorModel.isSelectGroupImageMode = true
    } else if (e === 'carouselImage' || e === 'carouselVideo') {
      selectorModel.carouselMaterialType = e === 'carouselImage' ? MATERIAL_TYPE.IMAGE : MATERIAL_TYPE.VIDEO
    } else {
      selectorModel.isNormalMode = true
    }

    // 初始化累计起始值
    increasingGroupIndex = Math.max(increasingGroupIndex, creativeFormGroups.value.length)

    if (selectorModel.isNormalMode) {
      selectorModel.selectorProps = {
        ...selectorModel.selectorProps,
        max: 15 - selectedMaterialLength.value,
        assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO],
        form: { ...makeSelectorForm(), sizes: ['1280*720', '720*1280', '1920*1080', '1080*1920'], media_id: MEDIAS.GDT }
      }
    }

    if (selectorModel.carouselMaterialType) {
      selectorModel.selectorProps = {
        ...selectorModel.selectorProps,
        max: Math.max(3 - selectedMaterialLength.value, 0),
        assetType: MATERIAL_TYPE_ID[selectorModel.carouselMaterialType],
        form: { ...makeSelectorForm(), sizes: ['1280*720', '720*1280', '1920*1080', '1080*1920'], media_id: MEDIAS.GDT }
      }
    }

    if (selectorModel.isUploadGroupImageMode) {
      selectorModel.selectorProps = {
        ...selectorModel.selectorProps,
        max: e,
        assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE],
        form: { ...makeSelectorForm(), sizes: ['800*800'], media_id: MEDIAS.GDT }
      }
    }

    if (selectorModel.isSelectWxgamePlayableMode) {
      selectorModel.selectorProps = {
        ...selectorModel.selectorProps,
        // 试玩模式且是智能分配时，不限制选择数量,如果只是试玩模式，只能选择1张图片
        max: selectorModel.isDistributionMode ? -1 : 1 - selectedMaterialLength.value,
        assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE],
        form: { ...makeSelectorForm(), sizes: ['1280*720'], media_id: MEDIAS.GDT }
      }
    }

    if (selectorModel.isSelectGroupImageMode) {
      selectorModel.selectorProps = {
        ...selectorModel.selectorProps,
        max: 15 - selectedMaterialLength.value,
        assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.GROUP_IMAGE],
        form: { ...makeSelectorForm(), sizes: [], media_id: MEDIAS.GDT }
      }
    }

    // 如果是智能分配模式，每次打开弹窗都重置分配配置
    // 20260114试玩模式的智能分配走上面的if判断
    if (selectorModel.isDistributionMode && !selectorModel.isSelectWxgamePlayableMode) {
      const distributionMaterialType = selectorModel.carouselMaterialType || MATERIAL_TYPE.VIDEO
      selectorModel.selectorProps = {
        ...selectorModel.selectorProps,
        max: -1,
        assetType: MATERIAL_TYPE_ID[distributionMaterialType],
        form: { ...makeSelectorForm(), sizes: ['1280*720', '720*1280', '1920*1080', '1080*1920'], media_id: MEDIAS.GDT }
      }

      resetDistributeForm()
      if (selectorModel.carouselMaterialType) {
        selectorModel.distributeForm[MATERIAL_TYPE.VIDEO] = selectorModel.carouselMaterialType === MATERIAL_TYPE.VIDEO ? 3 : 0
        selectorModel.distributeForm[MATERIAL_TYPE.IMAGE] = selectorModel.carouselMaterialType === MATERIAL_TYPE.IMAGE ? 3 : 0
        selectorModel.distributeForm[MATERIAL_TYPE.GROUP_IMAGE] = 0
      }
    }

    // 素材选择弹窗媒体素材标签参数，是否根据素材标签过滤
    if (materialTagList.value?.length) {
      selectorModel.selectorProps.form.media_tag = materialTagList.value[0].id
    }
    selectorModel.showSelectorModal = true
  }

  /**
 * 核心分配引擎 (distributeItemsRandom)
 */
  function distributeItemsRandom(data, options = {}) {
    const {
      numArrays = 1,
      targetPerArray = 0,
      shuffleGroups = true,
      shuffleItems = true
    } = options

    const result = Array.from({ length: numArrays }, () => [])
    let groupIds = Object.keys(data)
    if (shuffleGroups) groupIds = shuffle(groupIds)

    for (const groupId of groupIds) {
      let items = data[groupId]
      if (shuffleItems) items = shuffle(items)

      const itemsWithParent = items.map(item => ({ ...item, _groupId: groupId }))
      let placed = false

      // 尝试整体放入
      for (let i = 0; i < numArrays; i++) {
        if (result[i].length + itemsWithParent.length <= targetPerArray) {
          result[i].push(...itemsWithParent)
          placed = true
          break
        }
      }

      // 无法整体放入则拆分填充
      if (!placed) {
        const remainingItems = [...itemsWithParent]
        for (let i = 0; i < numArrays && remainingItems.length > 0; i++) {
          const available = targetPerArray - result[i].length
          if (available > 0) {
            result[i].push(...remainingItems.splice(0, available))
          }
        }
      }
    }
    return result
  }

  function distributeMaterialsSecure(materials, distributeForm) {
    const { group, groupAsSameImageId = false, ...restDistributeForm } = distributeForm
    const numGroups = group || 0
    if (numGroups <= 0) throw new Error('分配[创意组]必须大于 0')
    if (!restDistributeForm.video && !restDistributeForm.image && !restDistributeForm.group_image) throw new Error('请至少输入一个素材类型的分配个数')

    // const resourceTypes = ['video', 'image', 'group_image']
    const resourceTypes = Object.keys(restDistributeForm)
    const errors = []
    const warnings = []

    // --- 1. 预检：数量校验 ---
    resourceTypes.forEach(type => {
      const requiredEach = restDistributeForm[type] || 0
      const totalRequired = requiredEach * numGroups
      const totalAvailable = materials[type]?.length || 0

      if (totalAvailable < totalRequired) {
        const missing = totalRequired - totalAvailable
        errors.push(`[${MATERIAL_TYPE_NAME[type]}] 数量不足：需要${totalRequired}，实际${totalAvailable}，缺 ${missing}个；`)
      }
    })

    // 如果有任何资源不足，抛出异常或根据业务逻辑决定是否继续
    if (errors.length > 0) {
      // console.error('❌ 分配失败，资源库存不足：\n' + errors.join('\n'))
      // 这里可以选择 throw Error，或者只打印警告继续分配能分配的部分
      throw new Error('分配失败，选择素材不足：\n' + errors.join('\n'))
    }

    // --- 2. 准备数据结构 ---

    // 视频：按 imageId 聚合
    const videoData = {}
    if (groupAsSameImageId) {
      (materials.video || []).forEach(v => {
        if (!videoData[v.imageId]) videoData[v.imageId] = []
        videoData[v.imageId].push(v)
      })
    } else {
      // 视频：按 ID 独立
      (materials.video || []).forEach(v => { videoData[v.id] = [v] })
    }
    console.log('videoData output: ', videoData)
    // 图片：按 ID 独立（模拟 distributeItemsRandom 需要的键值对结构）
    const imageData = {};
    (materials.image || []).forEach(img => { imageData[img.id] = [img] })

    // 组图：按 ID 独立
    const groupImageData = {};
    (materials.group_image || []).forEach(gi => { groupImageData[gi.id] = [gi] })

    // --- 3. 调用引擎分配 ---
    const videoResults = distributeItemsRandom(videoData, {
      numArrays: numGroups,
      targetPerArray: restDistributeForm.video
    })

    const imageResults = distributeItemsRandom(imageData, {
      numArrays: numGroups,
      targetPerArray: restDistributeForm.image
    })

    const groupImageResults = distributeItemsRandom(groupImageData, {
      numArrays: numGroups,
      targetPerArray: restDistributeForm.group_image
    })

    // --- 4. 合并并进行次级预警（针对视频 imageId 拆分情况） ---
    const finalResult = videoResults.map((vArr, index) => {
      const iArr = imageResults[index]
      const giArr = groupImageResults[index]

      // 检查该组是否达到了预期的数量
      if (vArr.length < restDistributeForm.video) {
        warnings.push(`第 ${index + 1} 组视频数量未达标: 期望 ${restDistributeForm.video}, 实际 ${vArr.length}`)
      }

      const combined = [...vArr, ...iArr, ...giArr]
      // return shuffle(combined) // 最终组内顺序随机
      return combined
    })

    if (warnings.length > 0) {
      console.warn('分配完成，但存在以下异常：\n' + warnings.join('\n'))
    }
    return finalResult
  }

  function distributeCarouselMaterials(materials, distributeForm, materialType) {
    const groupCount = Number(distributeForm.group) || 0
    const materialName = materialType === MATERIAL_TYPE.IMAGE ? '图片' : '视频'
    const carouselMaterials = materials[materialType] || []

    if (groupCount <= 0) throw new Error('分配[创意组]必须大于 0')
    if (carouselMaterials.some((material) => Number(material.width) <= 0 || Number(material.height) <= 0)) {
      throw new Error(`所选${materialName}素材缺少有效的宽高尺寸`)
    }

    const sizeBuckets = groupBy(carouselMaterials, getMaterialSizeKey)
    const result = []
    for (const bucket of Object.values(sizeBuckets)) {
      const availableMaterials = shuffle(bucket)
      while (availableMaterials.length >= 3 && result.length < groupCount) {
        result.push(availableMaterials.splice(0, 3))
      }
      if (result.length === groupCount) break
    }

    if (result.length < groupCount) {
      throw new Error(`同一宽高尺寸的${materialName}素材不足，无法创建${groupCount}个创意组，每组需要3个`)
    }

    return result
  }

  /**
   * @description: 素材选择确认前置处理、校验，如果通过校验，生成创意组
   * @param {*} done
   * @param {*} fail
   * @return {video: [], image: [], image_list: []}
   */
  const onSelectorBeforeSubmit = (seletedMaterials, callbacks) => {
    const { done, fail } = callbacks || {}
    const { distributeForm, isSelectWxgamePlayableMode, carouselMaterialType } = selectorModel
    // 试玩模式智能分配组的数量等于选择图片的数量，每个组只分配1张图片，配置UI已隐藏，这里做默认的逻辑
    if (isSelectWxgamePlayableMode) {
      distributeForm.group = seletedMaterials.length
      distributeForm[MATERIAL_TYPE.IMAGE] = 1
      distributeForm[MATERIAL_TYPE.VIDEO] = 0
      distributeForm[MATERIAL_TYPE.GROUP_IMAGE] = 0
    }

    if (carouselMaterialType) {
      const materialName = carouselMaterialType === MATERIAL_TYPE.IMAGE ? '图片' : '视频'
      const countPerGroup = Number(distributeForm[carouselMaterialType]) || 0
      if (seletedMaterials.some((item) => item.type !== carouselMaterialType)) {
        const message = `轮播三${materialName === '图片' ? '图' : '视频'}只能选择${materialName}素材`
        return fail ? fail(message) : Message.error(message)
      }
      if (countPerGroup !== 3) {
        const message = `每个创意组必须分配3个${materialName}素材`
        return fail ? fail(message) : Message.error(message)
      }
    }

    const video = seletedMaterials.filter((item) => item.type === MATERIAL_TYPE.VIDEO)
    const image = seletedMaterials.filter((item) => item.type === MATERIAL_TYPE.IMAGE)
    const groupImage = seletedMaterials.filter((item) => item.type === 'image_list')

    // 重置替换素材辅助参数
    const materials = {
      [MATERIAL_TYPE.VIDEO]: video,
      [MATERIAL_TYPE.IMAGE]: image,
      [MATERIAL_TYPE.GROUP_IMAGE]: groupImage
    }
    try {
      const results = carouselMaterialType
        ? distributeCarouselMaterials(materials, distributeForm, carouselMaterialType)
        : distributeMaterialsSecure(materials, distributeForm)
      done && done()

      return results
    } catch (e) {
      return Message.error({ duration: 8, content: e.message })
    }
  }

  function resetDistributeForm() {
    selectorModel.distributeForm = { ...getDistributeFormModel() }
  }

  /**
   * @description: 批量生成创意组
   * @param {*} e
   * @return {*}
   */
  const getAutoDistributeGroups = (e) => {
    const res = onSelectorBeforeSubmit(e)
    if (!res || typeof res !== 'object') return

    const newGroups = res.map(item => {
      const creativeFormItem = getCreativeFormModel()

      for (let i = 0; i < item.length; i++) {
        const asset = item[i]
        creativeFormItem.selectedMaterials[asset.type].push(asset)
      }

      return creativeFormItem
    })

    return newGroups
  }

  const onCarouselSelectorBeforeSubmit = (selectedMaterials, callbacks) => {
    const { done, fail } = callbacks || {}
    const materialType = selectorModel.carouselMaterialType
    const materialName = materialType === MATERIAL_TYPE.IMAGE ? '图片' : '视频'
    const remainingCount = Math.max(3 - selectedMaterialLength.value, 0)
    const missingCount = Math.max(remainingCount - selectedMaterials.length, 0)
    const currentMaterials = Object.values(currGroup.value.selectedMaterials).flat()
    const groupMaterials = [...currentMaterials, ...selectedMaterials]

    if (groupMaterials.some((item) => item.type !== materialType)) {
      return fail && fail(`轮播三${materialName === '图片' ? '图' : '视频'}只能选择${materialName}素材`)
    }

    if (selectedMaterials.length !== remainingCount) {
      return fail && fail(`每个创意组必须选择3个${materialName}素材，当前还需选择${missingCount}个`)
    }

    if (!hasSameMaterialSize(groupMaterials)) {
      return fail && fail(`同一创意组的3个${materialName}素材宽高尺寸必须一致`)
    }

    done && done()
  }

  function handleNormalMaterialSelect(e) {
    const selectedMaterials = {
      ...currGroup.value.selectedMaterials
    }
    const existAssets = Object.values(selectedMaterials).flat()
    const materialLimit = selectorModel.carouselMaterialType ? 3 : 15
    const selectableAssets = selectorModel.carouselMaterialType
      ? e.filter((item) => item.type === selectorModel.carouselMaterialType)
      : e
    const assets = selectableAssets.slice(0, Math.max(materialLimit - existAssets.length, 0))
    const newAssets = groupBy(assets, (i) => i.type)

    if (!newAssets.image) newAssets.image = []
    newAssets.image = selectedMaterials.image.concat(newAssets.image)

    if (!newAssets.video) newAssets.video = []
    newAssets.video = selectedMaterials.video.concat(newAssets.video)

    if (!newAssets.image_list) newAssets.image_list = []
    newAssets.image_list = selectedMaterials.image_list.concat(newAssets.image_list)

    currGroup.value = {
      ...currGroup.value,
      selectedMaterials: newAssets
    }

    nextTick(() => {
      // 创意表单传入的form不是响应式的，需要手动更新
      onUpdateCreativeForm(currGroup.value)
    })
  }

  /**
   * @description: 组图上传
   * @param {*} e
   * @return {*}
   */
  async function handleImageGroupMaterialSelect(imageList) {
    if (!imageList.length) return
    const { planForm, budgetForm } = configForm.value
    const [firstImage] = imageList
    const maxSize = Math.max(...imageList.map((img) => img.size))
    const groupImageMd5s = imageList.map((i) => {
      const [_, md5] = IMG_URL_MD5_REGEXP.exec(i.url) || []
      return md5
    }).filter(Boolean).join(',')
    const groupImageUrls = imageList.map((i) => i.url).join(',')
    const fileInfos = [
      {
        width: firstImage.width,
        height: firstImage.height,
        nameSysFlag: 1,
        name: `${firstImage.name}.${firstImage.url.split('.').pop()}`,
        size: maxSize,
        groupImageMd5s,
        groupImageUrls
      }
    ]

    try {
      // 保存组图到素材库
      const [res] = await uploadMetarial({
        gameIds: planForm.game_id.id,
        imageStyles: IMAGE_STYLE.GRAPHIC,
        imageType: IMAGE_TYPES.GROUP_IMAGE,
        source: 1,
        lauchTime: moment().format('YYYY-MM-DD'),
        fileInfos,
        tagId: budgetForm.material_package_id
      })

      const { imageDetailId } = res
      const selectedMaterials = {
        ...currGroup.value.selectedMaterials
      }
      if (!selectedMaterials.image_list) {
        selectedMaterials.image_list = []
      }

      selectedMaterials.image_list.push({
        id: imageDetailId,
        // 占位图片
        preview_url: 'https://ptom-resource-cos.caohua.com/ptom/advResource/video/209159/75ea19ac3e03ce8c0e4f8b6586189cbd.png',
        type: 'image_list',
        placeholder: true
      })
      currGroup.value = {
        ...currGroup.value,
        selectedMaterials: selectedMaterials
      }

      nextTick(() => {
        // 创意表单传入的form不是响应式的，需要手动更新
        onUpdateCreativeForm(currGroup.value)
      })
    } catch (e) {
      console.log('uploadMetarial error output: ', e)
    }
  }

  /**
   * @description: 素材选择确认，并生成创意组
   * @param {*} e
   * @return {*}
   */
  const onSelectorConfirm = (e) => {
    const { isDistributionMode, isUploadGroupImageMode, replaceProps } = selectorModel
    // 素材替换处理
    if (replaceProps.materialType) {
      const { materialType, materialTabIndex, groupImageIndex } = replaceProps
      if (materialType === MATERIAL_TYPE.POSTER) {
        // 视频封面替换
        const [poster] = e
        const curr = unref(currGroup)

        curr.selectedMaterials.video = curr.selectedMaterials.video.map((item, index) => {
          if (index === materialTabIndex) {
            return {
              ...item,
              cover_id: poster.id,
              cover_height: poster.height,
              cover_width: poster.width,
              cover_url: poster.preview_url,
              cover_size: poster.size,
              cover_replaced: true,
              cover_replaced_type: poster.type
            }
          }
          return item
        })

        onUpdateCreativeForm(curr)
      } else if (materialType === MATERIAL_TYPE.VIDEO) {
        // 视频封面替换
        const [video] = e
        const curr = unref(currGroup)
        curr.selectedMaterials.video = curr.selectedMaterials.video.map((item, index) => {
          if (index === materialTabIndex) {
            return { ...video }
          }
          return item
        })

        onUpdateCreativeForm(curr)
      } else if (materialType === MATERIAL_TYPE.IMAGE && groupImageIndex === -1) {
        // 常规图片替换
        const [image] = e
        const curr = unref(currGroup)
        curr.selectedMaterials.image = curr.selectedMaterials.image.map((item, index) => {
          if (index === materialTabIndex) {
            return { ...image }
          }
          return item
        })

        onUpdateCreativeForm(curr)
      } else if (materialType === MATERIAL_TYPE.IMAGE && groupImageIndex > -1) {
        // 组图替换
        const [image] = e
        const curr = unref(currGroup)

        curr.selectedMaterials.image_list = curr.selectedMaterials.image_list.map((item, index) => {
          if (index === materialTabIndex) {
            return item.map((i, index) => {
              if (index === groupImageIndex) {
                return { ...image }
              }
              return i
            })
          }
          return item
        })

        onUpdateCreativeForm(curr)
      }
    } else {
      // 正常选择素材处理

      // 处理智能批量添加创意组
      if (isDistributionMode) {
        const newGroups = getAutoDistributeGroups(e)
        if (newGroups?.length) {
          //  更新创意组
          creativeFormGroups.value = [
            ...creativeFormGroups.value,
            ...newGroups.map((group) => {
              increasingGroupIndex++
              return { ...group, creative_name: `创意组${increasingGroupIndex}` }
            })
          ].slice(0, 200)
          // 更新全局计划配置

          updateConfigForm({
            ...configForm.value,
            recombineHelpers: {
              distributeForm: clone(selectorModel.distributeForm),
              distributeOriginIds: e.map(image => image.id)
            }
          })

          // 一键打乱始终使用最后一次智能分配的配置信息
          selectorModel.recombineDistrubuteForm = clone(selectorModel.distributeForm)
        }
      } else if (isUploadGroupImageMode) {
        // 处理组图手动添加素材
        handleImageGroupMaterialSelect(e)
      } else {
        // 处理常规手动添加素材
        handleNormalMaterialSelect(e)
      }
    }
  }

  const onSelectorClose = () => {
    selectorModel.showSelectorModal = false

    // 重置替换素材辅助参数
    selectorModel.replaceProps = {
      materialType: null,
      materialTabIndex: null,
      groupImageIndex: -1
    }

    selectorModel.isDistributionMode = false
    selectorModel.isUploadGroupImageMode = false
    selectorModel.isSelectWxgamePlayableMode = false
    selectorModel.carouselMaterialType = ''
    selectorModel.isSelectGroupImageMode = false
    selectorModel.isNormalMode = false
  }

  /**
   * @description: 重新打乱创意组
   * @return {*}
   */
  const onCreativeGroupsReCombine = async () => {
    const { creatives, recombineHelpers: helper } = configForm.value
    if (selectorModel.recombineLoading) return

    selectorModel.recombineLoading = true

    // 重新打乱的创意组个数等于已有创意组的个数
    selectorModel.distributeForm = {
      ...selectorModel.recombineDistrubuteForm,
      group: creatives.length
    }

    try {
      const imageDetails = helper.distributeOriginIds ? await queryImageDetailByIds({ imageDetailIds: helper.distributeOriginIds }) : []
      if (!imageDetails?.length) { selectorModel.recombineLoading = false; return }
      const existCreatives = imageDetails.map(image => {
        const { imageType } = image.items[0]
        if (imageType === IMAGE_TYPES.VIDEO) return dataFormatter.video(image.items[0])
        if (imageType === IMAGE_TYPES.IMAGE) return dataFormatter.image(image.items[0])
        if (imageType === IMAGE_TYPES.GROUP_IMAGE) return dataFormatter.group_image(image.items[0])
      })

      const newCreativeGroups = getAutoDistributeGroups(existCreatives)
      const recombineHelpers = {
        ...helper,
        distributeForm: clone(selectorModel.distributeForm)
      }
      updateConfigForm({
        ...configForm.value,
        recombineHelpers,
        creatives: newCreativeGroups.map((group, index) => {
          // 沿用之前的创意组名称
          return { ...group, creative_name: configForm.value.creatives[index].creative_name }
        })
      })

      setTimeout(() => {
        selectorModel.recombineLoading = false
      }, 500)
    } catch (e) {
      selectorModel.recombineLoading = false
    }
  }
  return {
    ...toRefs(selectorModel),
    seletedMaterialRadioDiasbled,
    selectedMaterialLength,
    materialTagList,
    onShowCreativeSelector,
    onShowRepalceMaterialSelector,
    onSelectorBeforeSubmit,
    onCarouselSelectorBeforeSubmit,
    getAutoDistributeGroups,
    onSelectorClose,
    onSelectorConfirm,
    onCreativeGroupsReCombine
  }
}

/**
 * @description: 批量导入素材
 * @return {*}
 */
export const useCreativeImportModal = () => {
  const assetsSelectorRef = ref(null)
  const creativeImportModel = reactive({
    showImportModal: false
  })
  const onShowImportModal = () => {
    creativeImportModel.showImportModal = true
  }

  const onCreativeImportConfirm = async (vals) => {
    try {
      const { list, msg } = await getCreativesByImageCodes({ imageNames: vals, mediaId: MEDIAS.GDT })
      const materialTypes = ['', 'image', 'video']

      if (list) {
        const rows = list.map((i) => ({ ...i.items[0], isFavor: i.isFavor })).map((i) => dataFormatter[materialTypes[i.imageType]](i))
        assetsSelectorRef.value.setSelectedRows(rows)
        MessageBox.alert(msg, '批量导入结果', {
          confirmButtonText: '知道了',
          callback: (action) => { }
        })
      }
    } catch (e) {
      console.log('eeeeeeeeeee output: ', e)
    }
  }

  return {
    ...toRefs(creativeImportModel),
    assetsSelectorRef,
    onShowImportModal,
    onCreativeImportConfirm
  }
}
